import React, { useState } from 'react';
import { Box, Grid2, Paper, Typography, Button, Select, MenuItem, CircularProgress, Stack, Chip } from '@mui/material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useParams } from 'react-router-dom';
import { translate as t } from 'hooks/useTranslations';
import { useManageStructureContext } from './context';
import { AWB } from '../../../../_types/awb';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import SortableItem from 'helpers/sortableItems';
import DraggableItem from './_components/DraggableItem';
import { neutral, primary } from '_lex/_foundations/phxColors';

const ManageStructure: React.FC = () => {
  const { id: accreditationId, versionId } = useParams();
  const {
    reorderedVersion,
    selectedStandard,
    selectedSubStandard,
    isSaving,
    error,
    handleStandardClick,
    handleSubStandardClick,
    handleDragEndStandards,
    handleDragEndSubStandards,
    handleDragEndRequirements,
    handleSave,
    getFilteredItems,
    originalVersion,
  } = useManageStructureContext();

  if (error) {
    return (
      <TakeoverLayout
        pageTitle={t('awb.standards.manageStructure.title')}
        backUrl={`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards`}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh',
            gap: 2,
          }}
        >
          <Typography
            variant='h6'
            color='error'
          >
            {error}
          </Typography>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </Box>
      </TakeoverLayout>
    );
  }

  if (!reorderedVersion) {
    return (
      <TakeoverLayout
        pageTitle={t('awb.standards.manageStructure.title')}
        backUrl={`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards`}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      </TakeoverLayout>
    );
  }

  const filteredStandards = getFilteredItems('standards') as AWB.Standard[];
  const filteredSubStandards = getFilteredItems('subStandards') as AWB.Standard[];
  const filteredRequirements = getFilteredItems('requirements') as AWB.Standard[];

  return (
    <TakeoverLayout
      pageTitle={t('awb.standards.manageStructure.title')}
      backUrl={`/admin/awb/accrediting-bodies/${accreditationId}/versions/${versionId}/standards`}
      contentHeaderLeft={
        <Stack>
          <Typography variant='subtitleLg'>{originalVersion?.version}</Typography>
          <Typography variant='bodySm'>{t('awb.standards.manageStructure.subtitle')}</Typography>
        </Stack>
      }
      contentHeaderRight={
        <Stack
          direction='row'
          spacing={2}
          sx={{ alignItems: 'center' }}
        >
          {isSaving && <CircularProgress size={20} />}
          <Button
            variant='contained'
            color='primary'
            onClick={handleSave}
            disabled={isSaving}
          >
            {t('common.save')}
          </Button>
        </Stack>
      }
    >
      <Grid2
        container
        spacing={3}
        sx={{ height: '100%' }}
      >
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Paper
            sx={{
              p: 0.5,
              borderRadius: 3,
              border: '1px solid #e4e8e8',
              backgroundColor: '#f2f6fa',
              height: 'fit-content',
              maxHeight: '80vh',
            }}
          >
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e4e8e8',
                maxHeight: '78vh',
                overflowY: 'auto',
              }}
            >
              <Typography
                variant='h6'
                sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}
              >
                {t('awb.standards.manageStructure.columns.standards')}
                <Chip
                  label={filteredStandards.length}
                  size='small'
                  sx={{
                    backgroundColor: neutral[200],
                    color: neutral[700],
                    minWidth: 24,
                    height: 24,
                  }}
                />
              </Typography>

              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEndStandards}
              >
                <SortableContext
                  items={filteredStandards.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {filteredStandards.map((standard) => (
                    <SortableItem
                      key={standard.id}
                      id={standard.id}
                    >
                      <DraggableItem
                        item={standard}
                        title={standard.title}
                        itemType='standard'
                        isSelected={selectedStandard === standard.id}
                        handleStandardClick={handleStandardClick}
                      />
                    </SortableItem>
                  ))}
                </SortableContext>
              </DndContext>
            </Paper>
          </Paper>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }}>
          <Paper
            sx={{
              p: 0.5,
              borderRadius: 3,
              border: '1px solid #e4e8e8',
              backgroundColor: '#f2f6fa',
              height: 'fit-content',
              maxHeight: '80vh',
            }}
          >
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e4e8e8',
                maxHeight: '78vh',
                overflowY: 'auto',
              }}
            >
              <Typography
                variant='h6'
                sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}
              >
                {t('awb.standards.manageStructure.columns.subStandards')}
                <Chip
                  label={filteredSubStandards.length}
                  size='small'
                  sx={{
                    backgroundColor: neutral[200],
                    color: neutral[700],
                    minWidth: 24,
                    height: 24,
                  }}
                />
              </Typography>

              {!selectedStandard ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t('awb.standards.manageStructure.selectStandardFirst')}
                  </Typography>
                </Box>
              ) : (
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEndSubStandards}
                >
                  <SortableContext
                    items={filteredSubStandards.map((s) => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredSubStandards.map((subStandard) => (
                      <SortableItem
                        key={subStandard.id}
                        id={subStandard.id}
                      >
                        <DraggableItem
                          item={subStandard}
                          title={subStandard.title}
                          itemType='subStandard'
                          isSelected={selectedSubStandard === subStandard.id}
                          handleStandardClick={handleSubStandardClick}
                        />
                      </SortableItem>
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </Paper>
          </Paper>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }}>
          <Paper
            sx={{
              p: 0.5,
              borderRadius: 3,
              border: '1px solid #e4e8e8',
              backgroundColor: '#f2f6fa',
              height: 'fit-content',
              maxHeight: '80vh',
            }}
          >
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e4e8e8',
                maxHeight: '78vh',
                overflowY: 'auto',
              }}
            >
              <Typography
                variant='h6'
                sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}
              >
                {t('awb.standards.manageStructure.columns.requirements')}
                <Chip
                  label={filteredRequirements.length}
                  size='small'
                  sx={{
                    backgroundColor: neutral[200],
                    color: neutral[700],
                    minWidth: 24,
                    height: 24,
                  }}
                />
              </Typography>

              {!selectedSubStandard ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t('awb.standards.manageStructure.selectSubStandardFirst')}
                  </Typography>
                </Box>
              ) : (
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEndRequirements}
                >
                  <SortableContext
                    items={filteredRequirements.map((r) => r.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredRequirements.map((requirement) => (
                      <SortableItem
                        key={requirement.id}
                        id={requirement.id}
                      >
                        <DraggableItem
                          item={requirement}
                          title={requirement.title}
                          itemType='requirement'
                        />
                      </SortableItem>
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </Paper>
          </Paper>
        </Grid2>
      </Grid2>
    </TakeoverLayout>
  );
};

export default ManageStructure;
